import { init as initMuiComponents, RootShortcutProvider, LocalizationProvider } from "@itk/mui-components";
import { init as initReactForm } from "@itk/react-form";
import { CssBaseline, Stack } from "@mui/material";
import type {} from "@mui/x-data-grid-pro/themeAugmentation";
import i18n from "i18next";
import moment from "moment";
import { SnackbarProvider } from "notistack";
import numeral from "numeral";
import "numeral/locales/da-dk";
import { createRoot } from "react-dom/client";
import { initReactI18next } from "react-i18next";
import resources from "./resources.json";
import { RootRoutes } from "./routes";
import { store } from "./store";
import { Provider } from "react-redux";
import { configureOpenApis, configureUiBlock } from "./backend/configure-openapi";
import { ITKToastr, ErrorViewer } from "./components";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { CustomThemeProvider } from "./utils/providers/customThemeProvider";
import "src/utils/prototypes";

// Add custom color type here
type TCustomColors = {
  sidebarBackground: string;
  black: string;
  darkGrey: string;
  grey: string;
  grey2: string;
  lightGrey: string;
  lightGrey2: string;
  white: string;
  border: string;
  whiteFaint: string;
  whiteSurface: string;
  whiteChip: string;
  whiteMuted: string;
  whiteBorder: string;
  whiteStrong: string;
  whiteHover: string;
};

// OVerriding palette type to add custom colors
declare module "@mui/material/styles" {
  interface Palette {
    customColors: TCustomColors;
  }
  interface PaletteOptions {
    customColors?: TCustomColors;
  }
}

// Prototype extensions
String.prototype.lowerCaseIncludes = function (searchString) {
  return this.toLowerCase().includes(searchString.toLowerCase());
};

i18n
  .use(initReactI18next)
  .init({ resources, fallbackLng: "da", interpolation: { escapeValue: false } })
  .then(() => {
    initReactForm(i18n);
    initMuiComponents(i18n);
  });

configureOpenApis();
configureUiBlock(store);

moment.locale("en");
numeral.locale("da-dk");

const rootElement = document.getElementById("root");
if (rootElement === null) throw Error("Root element doesn't exist.");
const root = createRoot(rootElement);

root.render(
  <LocalizationProvider>
    <RootShortcutProvider>
      <Provider store={store}>
        <CustomThemeProvider>
          <SnackbarProvider>
            <DndProvider backend={HTML5Backend}>
              <CssBaseline />
              <Stack height="100vh" overflow="hidden">
                <RootRoutes />
              </Stack>
              <ErrorViewer />
              <ITKToastr />
            </DndProvider>
          </SnackbarProvider>
        </CustomThemeProvider>
      </Provider>
    </RootShortcutProvider>
  </LocalizationProvider>
);
